import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { PolygonsType } from "@bxtech/bx-ui-lib/components/Mapbox/types";

export type MapEnrichmentType =
  | "yield"
  | "fuel"
  | "water"
  | "soil"
  | "cropInputs"
  | "dataAvailability"
  | null;

export type CropInputCategory =
  | "fertilizer"
  | "totalPesticide"
  | "insecticide"
  | "fungicide"
  | "herbicide";

export interface ChoroplethThresholds {
  min: number;
  low: number;
  medium: number;
  high: number;
  max: number;
}

export interface MapTooltipItem {
  label: string;
  value: string | number | undefined | null;
}

/** Discrete color map for status-based coloring (e.g., data availability) */
export type DiscreteColorMap = Record<string, string>;

/** Initial bounds for map zoom [minLng, minLat, maxLng, maxLat] */
export type MapBounds = [number, number, number, number];

/** Center marker for map zoom/pan */
export interface MapCenterMarker {
  lat: string;
  lng: string;
  zoom?: number;
}

export interface SidebarMapConfig {
  enrichmentType: MapEnrichmentType;
  polygons?: PolygonsType[];
  centroids?: GeoJSON.FeatureCollection;
  mapPolygonsProperty?: string;
  mapPolygonsPropertyMultiplier?: number;
  mapChoroplethThresholds?: ChoroplethThresholds;
  mapTitle?: string;
  /** Tooltip title for the map section header */
  mapTooltipTitle?: string;
  /** Tooltip body for the map section header */
  mapTooltipBody?: string;
  mapTooltipItems?: (properties: any) => MapTooltipItem[];
  invertMapPolarity?: boolean;
  /** Custom colors for choropleth map fill [low, medium, high] */
  mapColors?: [string, string, string];
  /** Custom colors for choropleth map borders [low, medium, high] */
  mapBorderColors?: [string, string, string];
  isLoading?: boolean;
  // Crop inputs specific
  cropInputCategory?: CropInputCategory;
  cropInputCategoryOptions?: Array<{ label: string; value: CropInputCategory }>;
  onCropInputCategoryChange?: (category: CropInputCategory) => void;
  // Data availability map specific
  /** Discrete color map for status-based coloring (status string -> hex color) */
  discreteColorMap?: DiscreteColorMap;
  /** Label to show for fields with no data (null property value) */
  mapNoDataLabel?: string;
  /** Initial bounds to zoom/pan the map to [minLng, minLat, maxLng, maxLat] */
  initialBounds?: MapBounds;
  /** Center marker to zoom/pan the map to a specific location */
  centerMarker?: MapCenterMarker;
  // Year selector for data availability
  /** Currently selected year */
  selectedYear?: number;
  /** Available years for the year selector */
  availableYears?: number[];
  /** Callback when year changes */
  onYearChange?: (year: number) => void;
  // Entity selector for data availability
  /** Currently selected entity (null = all entities) */
  selectedEntity?: string | null;
  /** Available entities for the entity selector */
  availableEntities?: Array<{ id: string; label: string }>;
  /** Callback when entity changes */
  onEntityChange?: (entity: string | null) => void;
  /** Custom discrete legend items (overrides the default numeric legend) */
  legendItems?: Array<{ color: string; label: string }>;
}

interface SidebarMapContextType {
  config: SidebarMapConfig;
  setMapConfig: (config: Partial<SidebarMapConfig>) => void;
  clearMapConfig: () => void;
}

const defaultConfig: SidebarMapConfig = {
  enrichmentType: null,
};

const SidebarMapContext =
  createContext<SidebarMapContextType | undefined>(undefined);

export const SidebarMapProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [config, setConfig] = useState<SidebarMapConfig>(defaultConfig);

  const setMapConfig = useCallback((newConfig: Partial<SidebarMapConfig>) => {
    setConfig((prev) => ({ ...prev, ...newConfig }));
  }, []);

  const clearMapConfig = useCallback(() => {
    setConfig(defaultConfig);
  }, []);

  const value = useMemo(
    () => ({ config, setMapConfig, clearMapConfig }),
    [config, setMapConfig, clearMapConfig]
  );

  return (
    <SidebarMapContext.Provider value={value}>
      {children}
    </SidebarMapContext.Provider>
  );
};

export const useSidebarMap = () => {
  const context = useContext(SidebarMapContext);
  if (!context) {
    throw new Error("useSidebarMap must be used within a SidebarMapProvider");
  }
  return context;
};

// Hook for dashboard pages to set their map config
export const useSidebarMapConfig = (config: Partial<SidebarMapConfig>) => {
  const { setMapConfig, clearMapConfig } = useSidebarMap();

  React.useEffect(() => {
    if (config.enrichmentType) {
      setMapConfig(config);
    }
    return () => {
      clearMapConfig();
    };
  }, [
    config.enrichmentType,
    config.polygons,
    config.mapPolygonsProperty,
    config.mapChoroplethThresholds,
    config.mapTitle,
    config.invertMapPolarity,
    config.isLoading,
    config.cropInputCategory,
    config.legendItems,
    setMapConfig,
    clearMapConfig,
  ]);
};
